import React, { useEffect, useState, useRef, useCallback } from 'react';
import Environment from '@config/environment';
import Modal from '@components/molecules/modal/modal';
import Icon from '@components/atoms/icon/icon';
import Typography from '@components/atoms/typography/typography';
import clsx from 'clsx';
import { APIProvider, Map, Marker, InfoWindow, useMarkerRef } from '@vis.gl/react-google-maps';
import { ShippingAddress } from '@interfaces/api/responses/user-shipping-address';
import useUser from '@hooks/user/use-user';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import {
  RequestDraftListParams,
  ResponseResult,
  getAddressSuggestions,
  getShippingAddressDropOffPoints,
} from '@services/my-orders-sales-service';
import { OrderPickupPoint, SuggestionLocation } from '@interfaces/models/timeline';
import { OrdersSalesQueryKeys } from '@enums/react-query-keys';
import { usePreferences } from '@context/preferences.context';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { useTranslation } from 'next-i18next';
import popinStyles from '../../popin.module.scss';
import styles from './select-carrier-options.module.scss';

const langPrefix = 'MY_ACCOUNT.ORDERS_SALES.POPUP.GET_SHIPPING_LABEL.';
const classNamePath = 'getShippingLabelModal__dropOff';
const mapIDView = 'get-shipping-label-map';
const mapApiKey = Environment.googleMapsApiKey || '';

// Custom hook for debounced API calls
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Custom Address Search Component
const AddressSearch: React.FC<{
  value: string;
  onChange: (value: string) => void;
  onSelect: (suggestion: SuggestionLocation) => void;
  placeholder: string;
  countryIso2: string;
}> = ({ value, onChange, onSelect, placeholder, countryIso2 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<SuggestionLocation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const debouncedValue = useDebounce(value, 400); // 400ms debounce
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch suggestions when debounced value changes
  useEffect(() => {
    if (debouncedValue && debouncedValue.length >= 3) {
      setIsLoading(true);
      getAddressSuggestions(debouncedValue, countryIso2)
        .then((res) => {
          if (res.kind === 'Success' && res.data) {
            const addressSuggestions = res.data.map((point) => ({
              placeId: point.placeId,
              address: point.address || '',
              city: point.city || '',
            }));
            setSuggestions(addressSuggestions);
          } else {
            setSuggestions([]);
          }
        })
        .catch(() => {
          setSuggestions([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setSuggestions([]);
    }
  }, [debouncedValue]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        if (event.key === 'Tab') {
          setIsOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    setIsOpen(true);
  };

  const handleInputFocus = () => {
    if (suggestions.length > 0 || isLoading) {
      setIsOpen(true);
    }
  };

  const handleInputBlur = () => {
    // Don't close immediately to allow option clicks
    // The click outside handler will handle closing
  };

  const handleSuggestionClick = (suggestion: SuggestionLocation) => {
    onChange(suggestion.address);
    onSelect(suggestion);
    setIsOpen(false);
  };

  return (
    <div
      ref={containerRef}
      className={styles[`${classNamePath}__form__field--inner`]}
      style={{ position: 'relative' }}
    >
      <Icon
        name="search"
        className={styles[`${classNamePath}__form__searchIcon`]}
      />
      <input
        type="text"
        value={value}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        placeholder={placeholder}
        className={styles[`${classNamePath}__form__field--input`]}
        autoComplete="off"
      />
      {isOpen && (suggestions.length > 0 || isLoading) && (
        <div className={styles[`${classNamePath}__form__field--dropdown`]}>
          {isLoading ? (
            <div className={styles[`${classNamePath}__form__field--dropdown-item`]}>Loading...</div>
          ) : (
            suggestions.map((suggestion) => (
              <div
                key={suggestion.placeId}
                className={styles[`${classNamePath}__form__field--dropdown-item`]}
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <div className={styles[`${classNamePath}__form__field--dropdown-item-name`]}>{suggestion.city}</div>
                <div className={styles[`${classNamePath}__form__field--dropdown-item-address`]}>
                  {suggestion.address}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export const Form: React.FC<{
  setGetPointsParams: (params: RequestDraftListParams) => void;
  productId: string;
  carrierName: string;
  address: ShippingAddress | null;
}> = ({ setGetPointsParams, productId, carrierName, address }) => {
  const { user } = useUser();

  const [search, setSearch] = useState(address?.line1 || user?.address.address1 || '');

  const { t } = useTranslation('orders-sales');

  const countryCode = address?.country?.iso2 || '';

  const handleAddressSelect = (suggestion: SuggestionLocation) => {
    // Update the search input with the selected address
    setSearch(suggestion.address);

    // Call the API to get drop-off points for the selected address
    setGetPointsParams({
      suggestionId: suggestion.placeId,
      productId: productId,
      countryCode: countryCode,
      carrierName: carrierName,
    });
  };

  return (
    <div className={styles[`${classNamePath}__form`]}>
      <div className={styles[`${classNamePath}__form__field`]}>
        <AddressSearch
          value={search}
          onChange={setSearch}
          onSelect={handleAddressSelect}
          placeholder={t(`${langPrefix}DROP_OFF.ADDRESS`)}
          countryIso2={countryCode}
        />
      </div>
    </div>
  );
};

export const List: React.FC<{
  selectedPointT: [string | null, (s: string | null) => void];
  getPointsR: UseQueryResult<ResponseResult<OrderPickupPoint[]>>;
  carrierName: string;
  carrierLogo?: string;
}> = ({ selectedPointT, getPointsR, carrierName, carrierLogo }) => {
  const [showOpenHours, setShowOpenHours] = useState<string[]>([]);
  const { t } = useTranslation('orders-sales');

  const { sendAnalyticEvent } = useAnalyticEvents('orders_sales_timeline');

  useEffect(() => {
    if (getPointsR.data?.kind === 'Success' && getPointsR.data.data.length > 0) {
      selectedPointT[1](getPointsR.data.data[0].address);
      setShowOpenHours([]);
    }
  }, [getPointsR.data]);

  if (getPointsR.isLoading) {
    return (
      <div className={styles[`${classNamePath}__list`]}>
        <div className={popinStyles.ordersSales__popin__loading} />
      </div>
    );
  }

  const data = getPointsR.data.kind === 'Success' ? getPointsR.data.data : [];
  if (data.length === 0) {
    return (
      <div className={styles[`${classNamePath}__list`]}>
        <div className={styles[`${classNamePath}__list--empty`]}>
          <Typography
            variant="body2"
            className={styles[`${classNamePath}__list--empty`]}
          >
            {t(`${langPrefix}DROP_OFF.NO_POINTS_FOUND`)}
          </Typography>
        </div>
      </div>
    );
  }

  const onCollectionPointClick = (collectionPoint: OrderPickupPoint) => {
    sendAnalyticEvent('drop_off_points_marker_select_carrier');
    selectedPointT[1](collectionPoint.address);
    document.getElementById(mapIDView)?.scrollIntoView({ behavior: 'smooth' });
  };

  const onSetShowOpenHours = (isOpen: boolean, collectionPoint: OrderPickupPoint) => {
    if (isOpen) {
      setShowOpenHours((prev) => prev.filter((id) => id !== collectionPoint.address));
    } else {
      sendAnalyticEvent('drop_off_points_click_show_opening_hours');
      setShowOpenHours((prev) => [...prev, collectionPoint.address]);
    }
  };

  const onOpenHourClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    isOpen: boolean,
    collectionPoint: OrderPickupPoint,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    onSetShowOpenHours(isOpen, collectionPoint);
  };

  const onOpenHourKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    isOpen: boolean,
    collectionPoint: OrderPickupPoint,
  ) => {
    // Detect Space key down to show open hours section
    if (e.key === ' ') {
      e.stopPropagation();
      e.preventDefault();
      onSetShowOpenHours(isOpen, collectionPoint);
    }
  };

  const carrierImage = carrierLogo || '';

  return (
    <div className={styles[`${classNamePath}__list`]}>
      {data.map((collectionPoint, idx) => {
        const openingTime = (collectionPoint.localizedOpeningTime || [])
          .map((time) => `${time.day}: ${time.times}`)
          .join('\n');
        const isActive = selectedPointT[0] === collectionPoint.address;
        const isOpen = showOpenHours.includes(collectionPoint.address);
        return (
          <button
            tabIndex={idx}
            key={collectionPoint.address}
            id={collectionPoint.address}
            className={clsx(
              styles[`${classNamePath}__list--item`],
              isActive ? styles[`${classNamePath}__list--item--active`] : '',
            )}
            onClick={() => onCollectionPointClick(collectionPoint)}
          >
            <div className={styles[`${classNamePath}__list--carrier`]}>
              <img
                alt={carrierName}
                src={carrierImage}
                className={styles[`${classNamePath}__list--carrier--image`]}
              />
              <div className={styles[`${classNamePath}__list--carrier--name`]}>{carrierName}</div>
            </div>

            <div className={styles[`${classNamePath}__list--info`]}>
              <span className={styles[`${classNamePath}__list--info--name`]}>{collectionPoint.name}</span>
              <span>{collectionPoint.address}</span>
              <span className={styles[`${classNamePath}__list--info--distance`]}>
                {collectionPoint.distance?.formatted}
                <span> - </span>
                <div
                  role="button"
                  tabIndex={idx}
                  className={styles[`${classNamePath}__list--info--show`]}
                  onClick={(e) => onOpenHourClick(e, isOpen, collectionPoint)}
                  onKeyDown={(e) => onOpenHourKeyDown(e, isOpen, collectionPoint)}
                >
                  {t(`${langPrefix}DROP_OFF.SHOW_OPENING_HOURS`)}
                  <Icon
                    className={styles[`${classNamePath}__list--info--showIcon`]}
                    name={isOpen ? 'small-dropdown-up-default' : 'small-dropdown-down-default'}
                  />
                </div>
              </span>
            </div>
            {isOpen && <div className={styles[`${classNamePath}__list--openingHours`]}>{openingTime}</div>}
          </button>
        );
      })}
    </div>
  );
};

// Custom MarkerWithInfoWindow Component
const MarkerWithInfoWindow: React.FC<{
  marker: {
    id: string;
    name: string;
    description: string;
    position: { lat: number; lng: number };
    icon?: string;
  };
  isSelected: boolean;
  onMarkerClick: (id: string) => void;
  onInfoWindowClose: () => void;
}> = ({ marker, isSelected, onMarkerClick, onInfoWindowClose }) => {
  const [markerRef, markerInstance] = useMarkerRef();

  const handleMarkerClick = useCallback(() => {
    onMarkerClick(marker.id);
  }, [marker.id, onMarkerClick]);

  const handleClose = useCallback(() => {
    onInfoWindowClose();
  }, [onInfoWindowClose]);

  return (
    <>
      <Marker
        ref={markerRef}
        position={marker.position}
        icon={{
          url: marker.icon,
          scaledSize: new google.maps.Size(30, 30),
        }}
        onClick={handleMarkerClick}
      />

      {isSelected && (
        <InfoWindow
          anchor={markerInstance}
          onClose={handleClose}
        >
          <div>
            <b>{marker.name}</b>
            <p style={{ textTransform: 'capitalize', marginTop: '10px', lineHeight: '20px' }}>{marker.description}</p>
          </div>
        </InfoWindow>
      )}
    </>
  );
};

export const MapWrapper: React.FC<{
  selectedPointT: [string | null, (s: string | null) => void];
  getPointsR: UseQueryResult<ResponseResult<OrderPickupPoint[]>>;
}> = ({ selectedPointT, getPointsR }) => {
  const { sendAnalyticEvent } = useAnalyticEvents('orders_sales_timeline');

  const [selectedMarkerId, setSelectedMarkerId] = selectedPointT;

  if (getPointsR.isLoading) {
    return (
      <div className={styles[`${classNamePath}__map`]}>
        <div className={clsx(popinStyles.ordersSales__popin__loading, styles[`${classNamePath}__map--loading`])} />
      </div>
    );
  }

  const data = getPointsR?.data?.kind === 'Success' ? getPointsR.data.data : [];
  const markers = data.map((point) => ({
    id: point.address,
    name: point.name,
    description: (point.localizedOpeningTime || []).map((time) => `${time.day}: ${time.times}`).join('\n'),
    position: { lat: point.location?.lat || 0, lng: point.location?.lng || 0 },
    icon: point.carrier?.iconUrl,
  }));

  const onMarkerClick = (id: string) => {
    sendAnalyticEvent('drop_off_points_marker_select_carrier');
    setSelectedMarkerId(id);
  };

  return (
    <div
      className={styles[`${classNamePath}__map`]}
      id={mapIDView}
    >
      <Map
        defaultZoom={15}
        defaultCenter={markers[0]?.position || { lat: 0, lng: 0 }}
      >
        {markers.map((marker) => (
          <MarkerWithInfoWindow
            key={marker.id}
            marker={marker}
            isSelected={selectedMarkerId === marker.id}
            onMarkerClick={onMarkerClick}
            onInfoWindowClose={() => setSelectedMarkerId(null)}
          />
        ))}
      </Map>
    </div>
  );
};

export const DropOff: React.FC<{
  onClose: () => void;
  address: ShippingAddress | null;
  carrierName: string;
  carrierLogo?: string;
  productId: string;
}> = ({ onClose, address, carrierName, carrierLogo, productId }) => {
  const { t } = useTranslation('orders-sales');

  const { sendAnalyticEvent } = useAnalyticEvents('orders_sales_timeline');

  const { language } = usePreferences();

  const [getPointsParams, setGetPointsParams] = useState<RequestDraftListParams>({
    productId: productId,
    countryCode: address?.country?.iso2 || '',
    carrierName: carrierName,
  });

  const selectedPointT = useState<string | null>(null);

  const getPointsR = useQuery<ResponseResult<OrderPickupPoint[]>>({
    queryKey: [OrdersSalesQueryKeys.GET_DROP_OFF_POINTS, getPointsParams],
    queryFn: () => getShippingAddressDropOffPoints(getPointsParams),
    staleTime: 0,
    cacheTime: 0,
  });

  useEffect(() => {
    sendAnalyticEvent('timeline_start_to_ship_view_drop_off_points');
  }, []);

  return (
    <Modal
      open={true}
      title={t(`${langPrefix}DROP_OFF.TITLE`)}
      onClose={onClose}
      className={styles[`${classNamePath}`]}
    >
      <div className={styles[`${classNamePath}__container`]}>
        <Form
          setGetPointsParams={setGetPointsParams}
          productId={productId}
          carrierName={carrierName}
          address={address}
        />
        <div className={styles[`${classNamePath}__listMap`]}>
          <List
            getPointsR={getPointsR}
            selectedPointT={selectedPointT}
            carrierName={carrierName}
            carrierLogo={carrierLogo}
          />
          <APIProvider
            apiKey={mapApiKey}
            language={language === 'hk' ? 'zh' : language}
          >
            <MapWrapper
              selectedPointT={selectedPointT}
              getPointsR={getPointsR}
            />
          </APIProvider>
        </div>
      </div>
    </Modal>
  );
};
